/* eslint-disable max-lines-per-function */
import { useStandortgruppe } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useStandortgruppen/useStandortgruppen';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import {
  createContext,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import { View } from '@/domain/mitarbeiter/features/rotationsplan/entities/views';
import {
  createRotationsplanFeatureStore,
  RotationsplanFeatureStore,
} from '@/domain/mitarbeiter/features/rotationsplan/features/shared/store';
import { useRotationsplaene } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useRotationsplaene/useRotationsplaene';
import { RotationsplanFeatureProvider } from '@/domain/mitarbeiter/features/rotationsplan/features/shared/RotationsplanFeatureContext';
import { useBerufe } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useBerufe/useBerufe';
import { Page } from '@/domain/mitarbeiter/pages/rotationsplan/overview/Page';
import { Standort } from '@/domain/mitarbeiter/features/rotationsplan/entities/Standort';
import { NavigateToV1 } from '@/domain/mitarbeiter/pages/rotationsplan/NavigateToV1';
import { FeedbackToggle } from '@/components/FeadbackToggle/FeedbackToggle';
import { LoadingPage } from '@/pages';
import { Standortgruppe } from '@/domain/mitarbeiter/features/rotationsplan/entities/standortgruppe';
import { Standort as ContextStandort } from '@/features/standortContext/entities/standort';
import {
  getPersistedFilterStandortUids,
  resolveFetchStandortIds,
} from '@/domain/mitarbeiter/features/rotationsplan/features/shared/fetchStandorte';

// eslint-disable-next-line react-refresh/only-export-components
export const StoreContext = createContext<RotationsplanFeatureStore | null>(
  null,
);

export function PageWrapper({ view }: { view: View }) {
  const { t } = useTranslation();
  const { currentStandort, standorte } = useStandortContext();
  const { standortGruppe } = useStandortgruppe(
    currentStandort?.idStandortgruppe,
  );
  const year = dayjs(new Date()).get('year').toString();
  const [isStandorteUpdatePending, startStandorteUpdateTransition] =
    useTransition();

  const [fetchStandortIds, setFetchStandortIds] = useState<string[]>(() => {
    const persistedUids = getPersistedFilterStandortUids();
    const resolved = persistedUids
      ? resolveFetchStandortIds(persistedUids, standorte)
      : [];
    return resolved.length > 0 ? resolved : [currentStandort.id];
  });

  const updateFetchStandorte = useCallback(
    (standortUids: string[]) => {
      startStandorteUpdateTransition(() => {
        setFetchStandortIds(resolveFetchStandortIds(standortUids, standorte));
      });
    },
    [standorte],
  );

  useEffect(() => {
    // If no filters are persisted, keep the fetch ids aligned with the currently selected Standort.
    if (getPersistedFilterStandortUids()) {
      return;
    }

    setFetchStandortIds((prev) =>
      prev.length === 1 && prev[0] === currentStandort.id
        ? prev
        : [currentStandort.id],
    );
  }, [currentStandort.id]);

  if (!standortGruppe || !standortGruppe.istRotationsplanAktiviert) {
    return <NavigateToV1 />;
  }

  return (
    <>
      <Helmet>
        <title>{t('domain.mitarbeiter.rotationsplan.title')}</title>
      </Helmet>
      <Suspense fallback={<LoadingPage />}>
        <RotationsplanContent
          fetchStandortIds={fetchStandortIds}
          updateFetchStandorte={updateFetchStandorte}
          isStandorteUpdatePending={isStandorteUpdatePending}
          standortGruppe={standortGruppe}
          currentStandort={currentStandort}
          view={view as View}
          year={year}
        />
      </Suspense>
    </>
  );
}

function RotationsplanContent({
  fetchStandortIds,
  updateFetchStandorte,
  isStandorteUpdatePending,
  standortGruppe,
  currentStandort,
  view,
  year,
}: {
  fetchStandortIds: string[];
  updateFetchStandorte: (standortUids: string[]) => void;
  isStandorteUpdatePending: boolean;
  standortGruppe: Standortgruppe;
  currentStandort: ContextStandort;
  view: View;
  year: string;
}) {
  const { t } = useTranslation();
  const { rotationsplaene } = useRotationsplaene(fetchStandortIds);
  const { berufe } = useBerufe(standortGruppe.id);

  const currentDefaultStandort = useMemo(
    () =>
      new Standort({
        id: currentStandort.uid,
        name: currentStandort.name,
        storenummer: currentStandort.storeNummer,
      }),
    [currentStandort.name, currentStandort.storeNummer, currentStandort.uid],
  );

  const store = useMemo(
    () =>
      createRotationsplanFeatureStore(
        rotationsplaene,
        berufe,
        standortGruppe?.standorte ?? [],
        currentDefaultStandort,
        year,
      ),
    [
      berufe,
      currentDefaultStandort,
      rotationsplaene,
      standortGruppe?.standorte,
      year,
    ],
  );

  return (
    <StoreContext.Provider value={store}>
      <RotationsplanFeatureProvider
        updateFetchStandorte={updateFetchStandorte}
        isStandorteUpdatePending={isStandorteUpdatePending}>
        <FeedbackToggle
          title={t('domain.mitarbeiter.rotationsplan.feedback.title')}
          fields={[
            {
              kind: 'rating',
              label: t('domain.mitarbeiter.rotationsplan.feedback.bewertung'),
              ratings: [
                {
                  value: 1,
                  label: t(
                    'domain.mitarbeiter.rotationsplan.feedback.nichtNutzbar',
                  ),
                },
                { value: 2 },
                {
                  value: 3,
                  label: t(
                    'domain.mitarbeiter.rotationsplan.feedback.teilweiseHilfreich',
                  ),
                },
                { value: 4 },
                {
                  value: 5,
                  label: t(
                    'domain.mitarbeiter.rotationsplan.feedback.klareArbeitserleichterung',
                  ),
                },
              ],
            },
            {
              kind: 'textarea',
              label: t(
                'domain.mitarbeiter.rotationsplan.feedback.verbesserungspotenzial',
              ),
              placeholder: '',
            },
            {
              kind: 'textarea',
              label: t(
                'domain.mitarbeiter.rotationsplan.feedback.groessterMehrwert',
              ),
              placeholder: '',
            },
          ]}
        />
        <Page view={view} isStandorteUpdatePending={isStandorteUpdatePending} />
      </RotationsplanFeatureProvider>
    </StoreContext.Provider>
  );
}
